import type { FC } from 'react';
import React, { useCallback, useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import Button from '@atlaskit/button/standard-button';
import { token } from '@atlaskit/tokens';
import ButtonGroup from '@atlaskit/button/button-group';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Heading from '@atlaskit/heading';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

const i18n = defineMessages({
	questionTitleHalfwayThroughTrial: {
		id: 'premium-onboarding.question-halfway-through-trial.title.pto2',
		description:
			'The text is shown as the heading question at the top of the Premium trial usefulness survey when it appears halfway through the trial in the feature guide modal. It asks the user what they think of the Premium features so far to collect qualitative feedback. There are no placeholders in this message. Explains the purpose of the survey question.',
		defaultMessage: 'What do you think of Premium features so far?',
	},
	usefulButton: {
		id: 'premium-onboarding.useful.button.pto2',
		description:
			'The text is shown on the positive response button in the Premium trial usefulness survey when it is displayed halfway through the trial in the feature guide modal. It lets the user indicate that they find the Premium features useful. There are no placeholders in this message. Explains the affirmative feedback option.',
		defaultMessage: '👍 Useful',
	},
	notUsefulButton: {
		id: 'premium-onboarding.not-useful.button.pto2',
		description:
			'The text is shown on the negative response button in the Premium trial usefulness survey when it is displayed halfway through the trial in the feature guide modal. It lets the user indicate that they do not find the Premium features useful. There are no placeholders in this message. Explains the negative feedback option.',
		defaultMessage: '👎 Not useful',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SurveyContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	padding: `${token('space.100')} ${token('space.200')}`,
	gap: token('space.150'),
	borderRadius: token('radius.medium'),
	background: token('color.background.selected'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonGroupWrapper = styled.div({
	display: 'inline-block',
});

interface PremiumTrialOnboardingSurveyProps {
	stopShowingSurvey: () => void;
	totalFeaturesUsed: number;
	trialDaysLeft: number;
	isSiteAdmin: boolean;
	pto2Attributes: {};
	isPTORTEnabled?: boolean;
}

export const PremiumTrialOnboardingSurvey: FC<PremiumTrialOnboardingSurveyProps> = ({
	stopShowingSurvey,
	totalFeaturesUsed,
	trialDaysLeft,
	isSiteAdmin,
	pto2Attributes,
	isPTORTEnabled = false,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { activationId } = useSessionData();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'rendered',
				actionSubject: 'premFeaturesSurvey',
				source: 'premiumTrialOnboardingModal',
				attributes: {
					trialDaysLeft,
					isSiteAdmin,
					numPremiumFeaturesUsed: totalFeaturesUsed,
					activationId,
					...pto2Attributes,
					isPTORTEnabled,
				},
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		totalFeaturesUsed,
		trialDaysLeft,
		isSiteAdmin,
		activationId,
		pto2Attributes,
		isPTORTEnabled,
	]);

	const onClickUsefulnessFeedback = useCallback(
		(usefulnessFeedback: string) => {
			stopShowingSurvey();
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'premFeaturesSurvey',
					source: 'premiumTrialOnboardingModal',
					attributes: {
						activationId,
						trialDaysLeft,
						numPremiumFeaturesUsed: totalFeaturesUsed,
						usefulnessFeedback,
						...pto2Attributes,
						isPTORTEnabled,
					},
				},
			}).fire();
		},
		[
			stopShowingSurvey,
			createAnalyticsEvent,
			activationId,
			trialDaysLeft,
			totalFeaturesUsed,
			pto2Attributes,
			isPTORTEnabled,
		],
	);

	return (
		<SurveyContainer>
			<Heading size="xsmall" as="h2">
				<FormattedMessage {...i18n.questionTitleHalfwayThroughTrial} />
			</Heading>
			<ButtonGroupWrapper>
				<ButtonGroup>
					<Button appearance="primary" onClick={() => onClickUsefulnessFeedback('yes')}>
						<FormattedMessage {...i18n.usefulButton} />
					</Button>
					<Button onClick={() => onClickUsefulnessFeedback('no')} appearance="default">
						<FormattedMessage {...i18n.notUsefulButton} />
					</Button>
				</ButtonGroup>
			</ButtonGroupWrapper>
		</SurveyContainer>
	);
};
